import { AdminHeader } from "../../components/header";
import { Pagination } from "../../components/pagination";
import { GuestDetails } from "../../components/admin/modals/guest-details.modal";
import type { Ticket, Stats } from "../../utils/types";
import { RequestPayout } from "../../components/admin/modals/request-payout.modal";
import { EventQR } from "../../components/admin/modals/event-qr.modal";
import { CustomSelect } from "../../components/custom.select";
import Skeleton from "react-loading-skeleton";
import { firebaseAuth, firestoredb } from "../../utils/firebase";
import { algoliasearch } from "algoliasearch";
import moment from "moment";
import exportFromJSON from "export-from-json";
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import { LoadingPage } from "../../components/loading.page";
import {
  formatFirestoreTimestamp,
  formatMoney,
  NAIRA_SYMBOL,
  statusColor,
  ticketTypeHolder,
} from "../../utils";
import { toast } from "react-toastify";
import { DashboardMobile } from "./admin.dashboard.mobile";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { isEventAuthVerified } from "./admin.login";

export function calculatePriceToDisplay(ticket: Ticket) {
  let price =
    (ticket?.priceAfterDiscount ?? ticket.price) -
      (ticket?.feeOnOrganiser ?? 0) / ticket.boughtAtOnce || 0;
  price = price > 0 ? price : 0;

  return (
    <>
      ₦{formatMoney(price)}{" "}
      {(ticket?.boughtAtOnce ?? 0) > 1 ? (
        <i
          data-toggle="tooltip"
          data-placement="top"
          title="The user bought more than ONE ticket"
          className="bi bi-info-circle"
        />
      ) : null}
    </>
  );
}

export function Dashboard({ type = "admin" }: { type?: string }) {
  const { eventId } = useParams();
  const [selectedGuest, setSelectedGuest] = useState<Ticket>();
  const [eventData, setEventData] = useState<DocumentData | null>(null);
  const [isExpiredEvent, setIsExpiredEvent] = useState<boolean | null>(null);
  const [selectedTicketType, setSelectedTicketType] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 15,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchedUserIds, setSearchedUserIds] = useState<string[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalAmountofTicketsSold: 0,
    totalTicketsNo: 0,
    guestsCheckedIn: 0,
    balance: 0,
    payout: 0,
  });
  const [selectedAmount, setSelectedAmount] = useState("All");
  const [amounts, setAmounts] = useState<{ text: string; value: string }[]>([
    { text: "All", value: "all" },
  ]);
  const [statuses, setStatuses] = useState<{ text: string; value: string }[]>([
    { text: "All", value: "all" },
  ]);
  const [ticketTypes, setTicketsTypes] = useState([
    { text: "All", value: "all" },
  ]);
  const [allTickets, setAllTickets] = useState<DocumentData[]>([]);
  const internalAdminVisiting = type === "internal-admin";

  useEffect(() => {
    if (internalAdminVisiting) return;
    // check for if user is authenticated!!
    const unsub = onAuthStateChanged(firebaseAuth, async (user) => {
      if (!user) return (window.location.href = "/admin/login");
      if (user) {
        if (!isEventAuthVerified(eventId ?? "")) {
          window.location.href = "/admin/login";
          toast.error("Event is not authenticated, log in!");
        }
      }
    });
    return () => unsub();
  }, [eventId, internalAdminVisiting]);

  useEffect(() => {
    if (!eventId) return;
    const getEventDetails = async () => {
      const eventRef = doc(firestoredb, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (!eventSnap.exists()) {
        toast.error("Event not found");
        await signOut(firebaseAuth);
        localStorage.clear();
        window.location.href = "/admin/login";
      }
      let eventEndsAt = eventSnap.data()?.endDate?.seconds;
      if (eventEndsAt) {
        eventEndsAt = new Date(eventEndsAt * 1000);
        eventEndsAt.setHours(23, 59, 59, 999);
      }
      if (!eventEndsAt) {
        const date = new Date(eventSnap.data()?.date?.seconds * 1000);
        date.setDate(date.getDate() + 1);
        date.setHours(0, 0, 0, 0);
        eventEndsAt = date;
      }
      setIsExpiredEvent(moment(eventEndsAt).isBefore(moment()) ?? false);
      setEventData({ id: eventSnap.id, ...eventSnap.data() });
    };
    getEventDetails();
  }, [eventId]);

  useEffect(() => {
    if (!debouncedSearchTerm.length) return;
    searchAlgolia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!eventData) return;
    setTableLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedAmount,
    selectedStatus,
    selectedTicketType,
    searchedUserIds,
    debouncedSearchTerm,
    eventData,
    isExpiredEvent,
  ]);

  useEffect(() => {
    // debounces search address and tracks when store address is entered
    if (!searchTerm.length) return setSearchedUserIds([]);
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    // fetch data every 1 minute
    const timeout = setInterval(() => {
      fetchData();
    }, 60000);
    return () => clearInterval(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpiredEvent, pagination.page]);

  const fetchData = async () => {
    try {
      if (
        selectedStatus.toLowerCase() === "did-not-attend" &&
        !isExpiredEvent
      ) {
        setTickets([]);
        setPagination({
          ...pagination,
          total: 0,
          page: 0,
        });
        return;
      }
      const conditionalConstraints: QueryConstraint[] =
        getConditionalConstraints();
      const ticketCollectionRef = collection(firestoredb, "tickets");
      const ticketQuerySnapshot = await getDocs(
        await getTicketsQuery(conditionalConstraints)
      );
      // all tickets snapshot
      const allTicketQuerySnapshot = await getDocs(
        query(
          ticketCollectionRef,
          where("eventId", "==", eventId),
          ...conditionalConstraints
        )
      );
      if (!allTickets.length) {
        const formattedAllTickets = await formatTicketWithUsers(
          allTicketQuerySnapshot
        );
        setAllTickets([...formattedAllTickets]);
        let ticketTypes = Array.from(
          new Set(
            allTicketQuerySnapshot.docs.map(
              (ticket) => ticket.data().ticketType
            )
          )
        ).filter((el: any) => el);
        ticketTypes = ticketTypes.map((ticket) => {
          const tk = eventData?.types?.find((el: any) => el.id === ticket);
          return { text: tk?.title, value: tk?.id };
        });
        setTicketsTypes([{ text: "All", value: "all" }, ...ticketTypes]);
      }
      const totalCount = allTicketQuerySnapshot.size;
      const checkedInGuests = allTicketQuerySnapshot.docs.filter((doc) => {
        const data = doc.data();
        return data.uses.length > 0;
      });
      const guestsCheckedIn = checkedInGuests.length;
      const tickets = await formatTicketWithUsers(ticketQuerySnapshot);
      const prices = Array.from(
        new Set(
          allTicketQuerySnapshot.docs.map((ticket) => ticket.data().price)
        )
      );
      const totalSoldTicketPrice = allTicketQuerySnapshot.docs.reduce(
        (acc: number, curr: QueryDocumentSnapshot<DocumentData>) => {
          const data = curr.data();
          let amount =
            (data.priceAfterDiscount ?? data.price ?? 0) -
            (data?.feeOnOrganiser / data?.boughtAtOnce || 0);
          amount = amount > 0 ? amount : 0;
          return acc + amount;
        },
        0
      );
      const totalPayout = await getTotalPayout();
      if (!conditionalConstraints.length) {
        setStats({
          ...stats,
          totalTicketsNo: allTicketQuerySnapshot.size,
          totalAmountofTicketsSold: totalSoldTicketPrice,
          payout: totalPayout,
          balance:
            totalSoldTicketPrice - totalPayout > 0
              ? totalSoldTicketPrice - totalPayout
              : 0,
          guestsCheckedIn,
        });
        let newAmounts = amounts
          .map((amount) => amount.value)
          .filter((amount) => !amount.includes("all"));
        newAmounts.push(...prices.map((price) => price.toString()));
        newAmounts = Array.from(new Set(newAmounts));
        setAmounts([
          { text: "All", value: "all" },
          ...newAmounts.map((amount) => ({
            text: `₦${formatMoney(Number(amount))}`,
            value: amount,
          })),
        ]);
        setStatuses(
          isExpiredEvent
            ? [
                { text: "All", value: "all" },
                ...dateRange.map((el) => ({
                  text: `Checked-in on ${moment(el).format("DD MMM")}`,
                  value: `checked-in || ${moment(el).parseZone().unix()}`,
                })),
                { text: "Did not attend", value: "did-not-attend" },
              ]
            : [
                { text: "All", value: "all" },
                { text: "Going", value: "going" },
                { text: "Checked-in", value: "checked-in" },
              ]
        );
      }
      setTickets(tickets);
      setPagination({
        ...pagination,
        total: totalCount,
        page: pagination?.page ?? 0,
      });
    } catch (error: unknown) {
      console.log(error);
      if (error instanceof Error) {
        console.log("Error keys:", Object.keys(error));
      }
      toast.error("Error fetching data from firebase");
    } finally {
      setTableLoading(false);
      setPageLoading(false);
    }
  };

  const getTicketsQuery = async (paramConstraints: QueryConstraint[]) => {
    const ticketConstraints = [where("eventId", "==", eventId)];
    const additionalConstraints = [
      ...ticketConstraints,
      orderBy("createdAt", "desc"),
      limit(pagination.limit),
    ];
    const ticketCollectionRef = collection(firestoredb, "tickets");
    const ticketQuery = query(
      ticketCollectionRef,
      ...additionalConstraints,
      ...paramConstraints
    );
    return ticketQuery;
  };

  const formatTicketWithUsers = async (querySnapshot: QuerySnapshot) => {
    // limited tickets snapshot
    let tickets = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
      } as Ticket;
    });
    tickets = await Promise.all(
      tickets.map(async (ticket) => {
        const docSnap = await getDoc(doc(firestoredb, "users", ticket.userId));
        const data = { ...ticket };
        if (docSnap.exists()) {
          const userData = docSnap.data();
          data.guest = {
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            email: userData.email || "",
            phone: userData.phoneNumber || "",
          };
        }
        return {
          ...data,
          price: ticket.price,
          status:
            selectedStatus.toLowerCase() === "did-not-attend" ||
            (!(ticket.uses ?? []).length && isExpiredEvent)
              ? "Did not attend"
              : (ticket?.uses ?? []).length > 0
              ? "Checked-in"
              : "Going",
          checkedInAt: ticket.uses ?? [],
        };
      })
    );
    return tickets;
  };

  const getTotalPayout = async () => {
    const payoutCollectionRef = collection(firestoredb, "payouts");
    const payoutQuerySnapshot = await getDocs(
      query(payoutCollectionRef, where("eventId", "==", eventId))
    );
    return payoutQuerySnapshot.docs.reduce(
      (acc: number, curr: QueryDocumentSnapshot<DocumentData>) => {
        const data = curr.data();
        return acc + (data.amount ?? 0);
      },
      0
    );
  };

  const handlePagination = async (page: number) => {
    try {
      setPagination({
        ...pagination,
        page,
      });
      setTableLoading(true);
      const skipQuery = await getTicketsQuery([
        limit((page > 0 ? page : 1) * pagination.limit),
      ]);
      const conditionalConstraints: QueryConstraint[] =
        getConditionalConstraints();
      const skipSnap = await getDocs(skipQuery);
      const docs = skipSnap.docs;
      const lastDoc = docs[docs.length - 1];
      let q;
      if (lastDoc && page)
        q = await getTicketsQuery([
          startAfter(lastDoc),
          ...conditionalConstraints,
        ]);
      else q = await getTicketsQuery([...conditionalConstraints]);
      const ticketQuerySnapshot = await getDocs(q);
      const nextTickets = await formatTicketWithUsers(ticketQuerySnapshot);
      setTickets(nextTickets);
    } catch (error) {
      toast.error("Cannot move to the page selected");
      console.log("pagination error", error);
    } finally {
      setTableLoading(false);
    }
  };

  const getTimestampRange = (start: number, end: number) => {
    const startDate = moment.unix(start).toDate();
    const endDate = moment.unix(end).toDate();
    let range: Date[] = [];
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    let current: Date = new Date(startDate);
    while (moment(current).isBefore(endDate)) {
      const start = new Date(current);
      range.push(start);
      current.setDate(current.getDate() + 1);
    }
    range.push(endDate);
    return range;
  };

  const dateRange = useMemo(() => {
    if (!eventData) return [];
    return getTimestampRange(
      eventData?.date?.seconds,
      eventData?.endDate?.seconds ?? eventData?.date?.seconds
    );
  }, [eventData]);

  const getConditionalConstraints = () => {
    const conditionalConstraints: QueryConstraint[] = [];
    if (selectedAmount.toLowerCase() !== "all") {
      conditionalConstraints.push(where("price", "==", Number(selectedAmount)));
    }
    if (selectedStatus.toLowerCase() !== "all") {
      const status = selectedStatus.split("||");
      if (status[0].toLowerCase() === "going")
        conditionalConstraints.push(where("uses", "==", []));
      if (status[0].trim().toLowerCase() === "checked-in") {
        if (status[1]) {
          conditionalConstraints.push(
            where(
              "usesDates",
              "array-contains",
              Timestamp.fromMillis(Number(status[1].trim()) * 1000)
            )
          );
        } else {
          conditionalConstraints.push(where("uses", "!=", []));
        }
      }
      if (status[0].toLowerCase() === "did-not-attend" && isExpiredEvent)
        conditionalConstraints.push(where("uses", "==", []));
    }
    if (
      selectedTicketType.toLowerCase() !== "all" &&
      selectedTicketType.toLowerCase() !== "general-admission"
    ) {
      conditionalConstraints.push(
        where("ticketType", "==", selectedTicketType)
      );
    }
    if (debouncedSearchTerm.length && searchedUserIds.length) {
      conditionalConstraints.push(
        where("userId", "in", searchedUserIds),
        where("eventId", "==", eventId)
      );
    }
    return conditionalConstraints;
  };

  const searchAlgolia = async () => {
    const algoliaClient = algoliasearch(
      `${process.env.REACT_APP_ALGOLIA_APP_ID}`,
      `${process.env.REACT_APP_ALGOLIA_API_KEY}`
    );
    const {
      results: [searchResults],
    } = await algoliaClient.search({
      requests: [
        {
          indexName: "Ding Wallet",
          query: debouncedSearchTerm,
        },
      ],
    });
    const { hits } = searchResults as any;
    const userIds = hits.map((hit: any) => hit.objectID);
    setSearchedUserIds(userIds);
  };

  const downloadData = () => {
    const ticketsWithUsers = allTickets.map((ticket) => {
      const body: any = {
        firstName: ticket.guest?.firstName,
        lastName: ticket.guest?.lastName,
        email: ticket.guest?.email,
        phone: ticket.guest?.phone,
        ticketType: ticketTitle(ticket as Ticket),
        price: `${NAIRA_SYMBOL}${formatMoney(ticket.price)}`,
        status: ticket.status,
        createdAt: formatFirestoreTimestamp(ticket.createdAt),
      };
      if (Object.keys(ticket.nonRequiredInfo).length) {
        Object.keys(ticket.nonRequiredInfo).forEach((key: string) => {
          body[key] = ticket.nonRequiredInfo[key];
        });
      }
      if (Object.keys(ticket.otherInfo).length) {
        Object.keys(ticket.otherInfo).forEach((key: string) => {
          body[key] = ticket.otherInfo[key];
        });
      }
      return body;
    });
    exportFromJSON({
      data: ticketsWithUsers,
      fileName: `${eventData?.title}-tickets`,
      exportType: exportFromJSON.types.csv,
    });
  };

  const ticketTitle = (item: Ticket) => {
    return (
      (eventData?.types ?? [])?.find(
        (el: any) => el?.id?.toLowerCase() === item?.ticketType?.toLowerCase()
      )?.title ??
      item?.ticketTitle ??
      item?.ticketType ??
      ""
    );
  };

  return pageLoading ? (
    <LoadingPage />
  ) : (
    <div className="dashboard">
      <AdminHeader />
      <div className="dashboard-header desktop">
        <div className="dashboard-header__title">
          <h1>{eventData?.title}</h1>
        </div>
        <div className="dashboard-header__actions">
          {/* <button className="btn">Raffle Draw</button> */}

          <button className="btn" onClick={downloadData}>
            Export Data (CSV)
          </button>
          <button
            className="btn"
            data-bs-toggle="modal"
            data-bs-target="#event-qr-modal"
            id="event-qr-modal-btn"
          >
            Event QR
          </button>
          <button
            className="btn active"
            onClick={() => {
              window.open(
                `${window.location.origin}/events/${eventId ?? ""}`,
                "_blank"
              );
            }}
          >
            Event Page
          </button>
        </div>
      </div>
      <div className="dashboard-content desktop">
        <div className="ticket-cards">
          <div className="tickets-board">
            <div className="ticket-container">
              <div className="title">Tickets</div>
              <div className="ticket-container__content">
                <div className="ticket-container__content-figures">
                  <h4 className="count">{formatMoney(stats.totalTicketsNo)}</h4>
                  <p>Number of tickets sold</p>
                </div>
              </div>
            </div>
            <div className="ticket-container">
              <div className="title">Guests checked-in</div>
              <div className="ticket-container__content">
                <div className="ticket-container__content-figures">
                  <h4 className="count">
                    {formatMoney(stats.guestsCheckedIn)}
                  </h4>
                  <p>Number of guests checked-in at least once.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="tickets-board">
            <div className="ticket-container">
              <div className="title">Ticket Sales</div>
              <div className="ticket-container__content">
                <div className="ticket-container__content-figures">
                  <h4 className="count">
                    ₦{formatMoney(stats.totalAmountofTicketsSold)}
                  </h4>
                  <p>Value of tickets sold</p>
                </div>
              </div>
            </div>
            <div className="ticket-container">
              <div className="title">Payouts</div>
              <div className="ticket-container__content">
                <div className="ticket-container__content-figures">
                  <h4 className="count">₦{formatMoney(stats.payout)}</h4>
                  <p>Money paid out to you.</p>
                </div>
                {stats.balance > 0 && !internalAdminVisiting ? (
                  <div className="ticket-container__content-action">
                    <button
                      className="btn btn-link"
                      data-bs-toggle="modal"
                      data-bs-target="#request-payout-modal"
                      id="request-payout-modal-btn"
                    >
                      Request payout
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="ticket-container">
              <div className="title">Balance</div>
              <div className="ticket-container__content">
                <div className="ticket-container__content-figures">
                  <h4 className="count">₦{formatMoney(stats.balance)}</h4>
                  <p>Your balance left with us.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="ticket-filters">
          <div className="ticket-filters__text-search">
            <i className="bi bi-search"></i>
            <input
              placeholder="Search name, email..."
              onChange={({ target: { value } }) => setSearchTerm(value)}
            />
          </div>
          <div className="ticket-filters__filter-search">
            <CustomSelect
              title="Ticket type:"
              titleColor=""
              selectedOption={
                ticketTypes.find((type) => type.value === selectedTicketType)
                  ?.text ?? "All"
              }
              options={ticketTypes}
              onChange={(value) => {
                setSelectedTicketType(value);
              }}
            />
            <CustomSelect
              title="Status:"
              titleColor=""
              selectedOption={
                statuses.find((status) => status.value === selectedStatus)
                  ?.text ?? "All"
              }
              options={statuses}
              onChange={(value) => {
                setSelectedStatus(value);
              }}
            />
            <CustomSelect
              title="Amount:"
              titleColor=""
              selectedOption={
                amounts.find((amount) => amount.value === selectedAmount)
                  ?.text ?? "All"
              }
              options={amounts}
              onChange={(value) => {
                setSelectedAmount(value);
              }}
            />
          </div>
        </div>
        <div className="ticket-table">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">First Name</th>
                <th scope="col">Last Name</th>
                <th scope="col">Email address</th>
                <th scope="col">Ticket type</th>
                <th scope="col">Amount paid</th>
                <th scope="col">Bought ticket on</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            {tableLoading ? (
              <tbody>
                {[1, 2, 3, 4, 5].map((_, key) => (
                  <tr key={key}>
                    <td colSpan={7}>
                      <Skeleton width={"100%"} style={{ marginTop: "5px" }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : tickets.length ? (
              <>
                <tbody>
                  {tickets.map((item, key) => (
                    <tr
                      key={key}
                      onClick={() => {
                        if (item.guest) {
                          setSelectedGuest(item);
                          document
                            .getElementById("guest-details-modal-btn")
                            ?.click();
                        } else return toast.error("No guest details found");
                      }}
                    >
                      <td>{item.guest?.firstName || "-"}</td>
                      <td>{item.guest?.lastName || "-"}</td>
                      <td className="small-case">{item.guest?.email || "-"}</td>
                      <td>
                        <span
                          className={`ticket-type status ${statusColor(
                            ticketTypeHolder(ticketTitle(item)).colorType
                          )}`}
                        >
                          {ticketTypeHolder(ticketTitle(item)).status}
                        </span>
                      </td>
                      <td>{calculatePriceToDisplay(item)}</td>
                      <td>{formatFirestoreTimestamp(item.createdAt) || "-"}</td>
                      <td>
                        <span className={`status ${statusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <tbody>
                <tr className="no-tickets">
                  <td colSpan={7}>
                    <div style={{ width: "100%" }}>No Data!</div>
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          {tickets.length ? (
            <Pagination
              pageCount={Number(Math.ceil(pagination.total / pagination.limit))}
              onPageChange={(el) => handlePagination(el)}
              page={Number(pagination.page)}
            />
          ) : null}
        </div>
      </div>
      <DashboardMobile
        eventData={eventData}
        stats={stats}
        tickets={tickets.map((el) => ({ ...el, ticketTitle: ticketTitle(el) }))}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loading={tableLoading}
        pagination={pagination}
        handlePagination={handlePagination}
        ticketTypes={ticketTypes}
        selectedTicketType={selectedTicketType}
        setSelectedTicketType={setSelectedTicketType}
        amounts={amounts}
        selectedAmount={selectedAmount}
        setSelectedAmount={setSelectedAmount}
        statuses={statuses}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        isInternalAdminVisiting={internalAdminVisiting}
        setSelectedGuest={setSelectedGuest}
      />
      <GuestDetails
        item={
          selectedGuest
            ? { ...selectedGuest, ticketTitle: ticketTitle(selectedGuest) }
            : null
        }
      />
      <RequestPayout balance={stats.balance ?? 0} eventData={eventData} />
      <EventQR event={eventData} />
    </div>
  );
}

export default Dashboard;
